import { Component, EventEmitter, Input, NgZone, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {
    AzavistaApiService, GroupedFieldsViewGlobalEntityType, GroupedFieldsViewParentEntityType,
    GroupedFieldsViewParentType, IEvent, IGetParentSettingResponse, IGroupedFieldsView, IStringIdEntity
} from '@azavista/servicelib';
import { FieldComponentType, IField, IObjectEnumInputFieldValue, IValueChangedEventArgs } from '@azavista/components/shared';
import { RequiredScopesService } from '../required-scopes.service';
import { AclService } from '../acl.service';
import { SettingEntityType, TranslationKey } from '../enums';
import { SharedService } from '../shared.service';
import { GlobalGroupedFieldsViewManager } from './global-grouped-fields-view-manager';
import { SpecialFilterFieldId } from './grouped-fields-order/enums';
import { GroupedFieldsOrderComponent } from './grouped-fields-order/grouped-fields-order.component';
import { IGroupedFieldsOrderComponentData, IGroupedFieldsOrderComponentResult } from './grouped-fields-order/interfaces';
import { GroupedFieldsViewService } from './grouped-fields-view.service';
import { IGroupedFieldsViewManager } from './interfaces';
import { ParentGroupedFieldsViewManager } from './parent-grouped-fields-view-manager';
import { SettingsService } from '../settings.service';
import { AzavistaExportedElementExporterDirective } from '@azavista/components/exported-element';

@Component({
    selector: 'azavista3-grouped-fields-view',
    templateUrl: 'grouped-fields-view-component.html',
    standalone: false
})
export class GroupedFieldsViewComponent
extends AzavistaExportedElementExporterDirective
implements OnInit {
    @Input() fieldsTitle: string;
    @Input() allFields: IField[];
    @Input() parentType: GroupedFieldsViewParentType;
    @Input() parentEntityId: string;
    @Input() parentEntityType: GroupedFieldsViewParentEntityType;
    @Input() globalEntityType: GroupedFieldsViewGlobalEntityType;
    @Input() entity: IStringIdEntity;

    @Output() viewSelected = new EventEmitter<IGroupedFieldsView>();

    views: IGroupedFieldsView[];
    currentView: IGroupedFieldsView;
    viewManager: IGroupedFieldsViewManager;
    isAdmin = false;
    filterFields: IField[];
    globalEventFields: IField[];
    viewField: IField;
    viewFieldValue: IObjectEnumInputFieldValue;
    canCreateView = false;

    constructor(
        private matDialog: MatDialog, private apiSvc: AzavistaApiService, private aclSvc: AclService,
        private sharedSvc: SharedService, private gfvSvc: GroupedFieldsViewService, private rsSvc: RequiredScopesService,
        private settingsSvc: SettingsService,
        protected ngZone: NgZone,
    ) {
        super(ngZone);
    }

    async ngOnInit(): Promise<void> {
        this.isAdmin = this.aclSvc.isAdmin();
        if (this.isAdmin) {
            this.canCreateView = true;
        } else {
            const scopes = this.gfvSvc.getViewsScopes(this.globalEntityType, this.parentEntityType);
            this.canCreateView = this.aclSvc.getAclObjectForCurrentUser(scopes).allowed;
        }
        if (this.globalEntityType) {
            this.viewManager = new GlobalGroupedFieldsViewManager(this.apiSvc);
            this.viewManager.init({ globalEntityType: this.globalEntityType });
        } else {
            this.viewManager = new ParentGroupedFieldsViewManager(this.apiSvc);
            this.viewManager.init({
                parentType: this.parentType, parentEntityId: this.parentEntityId, parentEntityType: this.parentEntityType
            });
        }
        const views = await this.viewManager.getViews();
        if (this.parentType === GroupedFieldsViewParentType.event) {
            // This is event context - both event and event participant views use event fields for filtering
            // By getting local event fields we will cover the case with global event fields too
            this.filterFields = await this.apiSvc.getAllEventFields(this.parentEntityId);
        } else {
            // Not event context - use fields that are provided (current object fields - contact/organization)
            this.filterFields = this.allFields;
        }
        this.views = this.getFilteredAndOrderedViews(views);
        const selectedViewSettings = await this.getSelectedViewSettings();
        const selectedViewId = selectedViewSettings?.settings[0]?.setting?.active_setting_id;
        if (selectedViewId) {
            this.currentView = this.views.find(x => x.id === selectedViewId);
        } else {
            this.currentView = this.getFirstMatchingView(this.views);
        }
        this.viewField = this.createViewField();
        let addDefaultView: boolean;
        if (this.isAdmin || this.views.length === 0 || this.canCreateView) {
            addDefaultView = true;
        } else {
            addDefaultView = false;
        }
        this.viewFieldValue = this.createViewFieldValue(this.views, this.currentView, addDefaultView);
        this.viewSelected.next(this.currentView);
    }

    private async getSelectedViewSettings(): Promise<IGetParentSettingResponse> {
        const settingEntityType: SettingEntityType = (this.globalEntityType || this.parentEntityType) as any;
        if (settingEntityType) {
            const viewSettings = await this.settingsSvc.getSelectedViewSettings(
                (this.globalEntityType || this.parentEntityType) as any,
                this.entity.id
            );
            return viewSettings;
        }
        return null;
    }

    private async saveSelectedViewSettings(selectedViewId: string): Promise<void> {
        await this.settingsSvc.saveSelectedViewSettings(
            (this.globalEntityType || this.parentEntityType) as any,
            this.entity.id,
            selectedViewId
        );
    }

    refreshViewFieldValue(): void {
        this.viewFieldValue = null;
        window.setTimeout(() => this.viewFieldValue = this.createViewFieldValue(this.views, this.currentView, this.canCreateView));
    }

    createViewField(): IField {
        const field = {
            id: '__viewField', name: '__viewField', component: FieldComponentType.objectEnum,
            schema: { type: 'object' }, editable: true
        } as IField;
        return field;
    }

    createViewFieldValue(views: IGroupedFieldsView[], currentView: IGroupedFieldsView, addNullItem: boolean): IObjectEnumInputFieldValue {
        let allFieldsItem: IGroupedFieldsView;
        if (addNullItem) {
            allFieldsItem = { id: '', name: this.sharedSvc.translate(TranslationKey.allFields) } as IGroupedFieldsView;
        }
        const availableViews = addNullItem ? [allFieldsItem, ...views] : views;
        const result: IObjectEnumInputFieldValue = {
            items: availableViews,
            selected: availableViews.find(x => x === currentView),
            displayProperty: 'name'
        };
        if (addNullItem && !result.selected) {
            result.selected = result.items[0];
        }
        return result;
    }

    // onViewSelected(change: MatSelectChange): void {
    //     this.viewSelected.next(change.value as IGroupedFieldsView);
    // }

    onViewValueChanged(args: IValueChangedEventArgs): void {
        this.currentView = args.value;
        this.saveSelectedViewSettings(this.currentView?.id);
        this.viewSelected.next(this.currentView);
    }

    getOrderedViews(views: IGroupedFieldsView[]): IGroupedFieldsView[] {
        // Simplifying multiple nested sorts - apply a number to each item according to existence of its parts
        const withValue: { value: number, view: IGroupedFieldsView }[] = [];
        for (const view of views) {
            const sharedViewValue = view.shared_view ? 4 : 0;
            const filterValue = view.filters[0]?.field_name ? 2 : 0;
            // Teams should be considered only if view is shared
            // If the view is not shared, teams should be considered not existing
            const teamsValue = (view.shared_view && (view.view_by_all || view.team_ids?.length)) ? 1 : 0;
            const value = sharedViewValue + filterValue + teamsValue;
            withValue.push({ value: value, view: view });
        }
        withValue.sort((first, second) => second.value - first.value);
        const ordered = withValue.map(x => x.view);
        return ordered;
    }

    getFilteredAndOrderedViews(views: IGroupedFieldsView[]): IGroupedFieldsView[] {
        if (this.isAdmin) {
            return views;
        }
        const filteredViews: IGroupedFieldsView[] = [];
        for (const view of views.filter(x => x)) {
            const filterFields = [...this.filterFields];
            if (this.parentEntityType === GroupedFieldsViewParentEntityType.event) {
                filterFields.push(this.gfvSvc.getEventSettingsPublishField());
            }
            if (this.objectMatchesFieldFilter(view, filterFields, this.entity)
                && this.currentUserMatchesTeams(view)) {
                filteredViews.push(view);
            }
        }
        const ordered = this.getOrderedViews(filteredViews);
        return ordered;
    }

    getFirstMatchingView(views: IGroupedFieldsView[]): IGroupedFieldsView {
        if (this.isAdmin) {
            // Admins should show no custom view by default
            return null;
        }
        return views.find(x => x);
    }

    currentUserMatchesTeams(view: IGroupedFieldsView): boolean {
        if (view.view_by_all) {
            return true;
        }
        if (this.isAdmin) {
            return true;
        }
        if (!view.team_ids || view.team_ids.length === 0) {
            return true;
        }
        const userTeamIds = this.aclSvc.getTeamIdsFromToken();
        for (const userTeamId of userTeamIds) {
            if (view.team_ids.indexOf(userTeamId) >= 0) {
                return true;
            }
        }
        return false;
    }

    // convertFieldIdFilterToFieldNameFilter(filter: IGroupedFieldsViewFilter): IGroupedFieldsFieldNameViewFilter {
    //     const result: IGroupedFieldsFieldNameViewFilter = { field_name: filter.field_id, values: filter.values };
    //     return result;
    // }

    objectMatchesFieldFilter(view: IGroupedFieldsView, allFields: IField[], obj: any): boolean {
        // const filters: IGroupedFieldsFieldNameViewFilter[] = view.filters || [this.convertFieldIdFilterToFieldNameFilter(view.filter)];
        if (!view.filters || view.filters.length === 0) {
            return true;
        }
        for (const filter of view.filters) {
            if (!filter || !filter.field_name) {
                continue;
            }
            const field = allFields.find(x => x.id === filter.field_name || x.name === filter.field_name);
            if (!field) {
                continue;
            }

            const fieldValues = filter.values || [];
            // Create array from object value
            let objValue: any[] = obj[field.name];

            if (field.id === SpecialFilterFieldId.eventSettingsPublish
                && this.parentEntityType === GroupedFieldsViewParentEntityType.event
            ) {
                objValue = (this.entity as IEvent).settings.publish as any;
            }
            if (!Array.isArray(objValue)) {
                objValue = [objValue];
            }
            for (const value of objValue) {
                if (fieldValues.indexOf(value) === -1) {
                    // Some of the object values is not found in the filter values
                    return false;
                }
            }
        }
        return true;
    }

    async onCreateNewOrEditExistingClicked(createNew: boolean): Promise<void> {
        if (!createNew && this.currentView) {
            // The user wants to edit existing view - check if the current user has rights to do edits
            if (!this.isAdmin) {
                // Current user is not admin - check the view for "Share view with other users" check box checked
                if (this.currentView.shared_view) {
                    // This view is shared and the user is not admin - edit is denied
                    this.sharedSvc.showConfirmationDialog(
                        this.sharedSvc.translate(TranslationKey.editView),
                        [this.sharedSvc.translate(TranslationKey.thisViewCantBeEditedByYou)],
                        true
                    );
                    return;
                }
            }
        }
        let showUseViewInAllEvents: boolean;
        let useViewInAllEvents: boolean;
        let availableFields: IField[];
        let filterFields: IField[];
        if (this.parentType === GroupedFieldsViewParentType.event) {
            if (createNew) {
                useViewInAllEvents = false;
            } else {
                // Edit of exising event view - don't ask which fields to use - get them from the view
                showUseViewInAllEvents = true;
                useViewInAllEvents = this.currentView.global_fields;
            }
            if (useViewInAllEvents) {
                if (this.parentEntityType === GroupedFieldsViewParentEntityType.event) {
                    // Global event fields for both filter and drag-drop fields
                    this.globalEventFields ||= await this.apiSvc.getAllGlobalEventFields();
                    filterFields = this.globalEventFields;
                    availableFields = this.globalEventFields;
                } else if (this.parentEntityType === GroupedFieldsViewParentEntityType.participant) {
                    // Global event fields for filter, builtin event participant felds for drag-drop fields
                    this.globalEventFields ||= await this.apiSvc.getAllGlobalEventFields();
                    filterFields = this.globalEventFields;
                    availableFields = await this.apiSvc.getAllEventParticipantFields(this.parentEntityId);
                    availableFields = availableFields.filter(x => x.builtin);
                }
            } else {
                // Use only for current event
                if (this.parentEntityType === GroupedFieldsViewParentEntityType.event) {
                    // Local event fields for both filter and drag-drop fields
                    availableFields = await this.apiSvc.getAllEventFields(this.parentEntityId);
                    filterFields = availableFields;
                } else if (this.parentEntityType === GroupedFieldsViewParentEntityType.participant) {
                    // Global event fields for filter, event participant fields for drag-drop fields
                    this.globalEventFields ||= await this.apiSvc.getAllGlobalEventFields();
                    filterFields = this.globalEventFields;
                    availableFields = await this.apiSvc.getAllEventParticipantFields(this.parentEntityId);
                }
            }
        } else {
            // Not in event scope - use provided fields for current details
            availableFields = this.allFields;
            filterFields = availableFields;
        }
        // We need to remove timezone field from possible fields because it is received as enum field
        // but artificially changed to object enum which causes problems later (it is not sown in event details anyway)
        availableFields = availableFields.filter(x => x.name !== 'timezone');
        filterFields = filterFields.filter(x => x.name !== 'timezone');
        this.sharedSvc.replaceProfileIdFieldWithProfileRelation(availableFields);
        this.sharedSvc.replaceOrganizationIdFieldWithOrganizationRelation(availableFields);
        const data: IGroupedFieldsOrderComponentData = {
            allFields: availableFields,
            useAvailableFieldsNames: this.parentEntityType === GroupedFieldsViewParentEntityType.participant,
            filterFields: filterFields,
            currentView: createNew ? null : this.currentView,
            showUseViewInAllEvents: showUseViewInAllEvents,
            useViewInAllEvents: useViewInAllEvents,
            parentEntityType: this.parentEntityType
        };
        this.matDialog.open<
            GroupedFieldsOrderComponent,
            IGroupedFieldsOrderComponentData,
            IGroupedFieldsOrderComponentResult
        >(
            GroupedFieldsOrderComponent, {
            data: data
        }).afterClosed().subscribe({
            next: result => this.processGroupedFieldsOrderComponentResult(result)
        });
    }

    async processGroupedFieldsOrderComponentResult(result: IGroupedFieldsOrderComponentResult): Promise<void> {
        if (!result) {
            return;
        }
        if (result.isDeleted && result.view.id) {
            await this.viewManager.deleteView(result.view.id);
            const index = this.views.findIndex(x => x.id === result.view.id);
            if (index >= 0) {
                this.views.splice(index, 1);
            }
            this.views = this.getFilteredAndOrderedViews(this.views);
            this.currentView = this.getFirstMatchingView(this.views);
            this.refreshViewFieldValue();
            this.viewSelected.next(this.currentView);
            return;
        }
        const isNew = !result.view.id;
        let view: IGroupedFieldsView;
        if (isNew) {
            view = await this.viewManager.createView(result.view);
            this.views.push(view);
        } else {
            view = await this.viewManager.updateView(result.view);
            const index = this.views.findIndex(x => x.id === view.id);
            if (index >= 0) {
                this.views[index] = view;
            }
        }
        this.views = this.getFilteredAndOrderedViews(this.views);
        if (this.views.find(x => x.id === view.id)) {
            this.currentView = view;
            this.viewSelected.next(view);
        }
        this.refreshViewFieldValue();
    }
}
